/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import axios, { AxiosResponse } from "axios";
import baseUrl from "..";
import {
	PAMediaRemovalBatchListResponse,
	PAMediaRemovalMethodResponse,
	PAMediaRemovalPayload,
} from "../../components/admin/postAutoclaveProcess/mediaRemoval/mediaRemoval.types";
import { authToken, authUserID, axiosInstance } from "../axiosConfig";
import { CleaningAgentResponse, ContainerCleaningPayload, PAContainerCleaningListResponse } from "../../components/admin/postAutoclaveProcess/mediaCleaning/mediaCleaning.types";
import { ContInspectionPayload, PhyInspContainerConditionResponse, PhyInspContainerListResponse } from "../../components/admin/postAutoclaveProcess/platePhysicalInspection/platePhysicalInspection.types";
import { AutoclaveEquipmentListResponse, ContainersSterilizationListResponse, PostSterilizationPayload } from "../../components/admin/postAutoclaveProcess/emptySterilization/emptySterilization.types";

const buildUrl = (
	apiEndpoint: string,
	params: Record<string, string | number | undefined>,
): string => {
	const filtered = Object.fromEntries(
		Object.entries(params).filter(([_, v]) => v !== undefined && v !== ""),
	);
	return `${baseUrl}/${apiEndpoint}?${new URLSearchParams(filtered as Record<string, string>)}`;
};

const HEADERS = {
	"x-auth-token": authToken,
	"x-userid": authUserID,
};

const getMediaBatches = async (
	urlParams: any,
): Promise<PAMediaRemovalBatchListResponse> => {
	const url = buildUrl("media/list_media_preparation_batch?sort=id&order=desc", urlParams);
	const response: AxiosResponse<PAMediaRemovalBatchListResponse> =
		await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
};

const getContainers = async (urlParams: any) => {
	const url = buildUrl("media_post_autoclave/list_plates", urlParams);
	const response: AxiosResponse<any> = await axiosInstance.get(url, {
		headers: HEADERS,
	});
	return response.data;
};

const postMediaRemoval = async (
	payload: PAMediaRemovalPayload,
): Promise<boolean> => {
	const url = buildUrl("media_removal/save", {});
	const response: AxiosResponse<any> = await axiosInstance.post(url, payload, {
		headers: HEADERS,
	});
	return response.data.statusCode === 200;
};

const getMediaRemovalMethods =
	async (): Promise<PAMediaRemovalMethodResponse> => {
		const url = buildUrl("media_removal/removal_methods", {});
		const response: AxiosResponse<PAMediaRemovalMethodResponse> =
			await axiosInstance.get(url, {
				headers: HEADERS,
			});
		return response.data;
	};

const getMediaCleaningContainerList = async (urlParams: any): Promise<PAContainerCleaningListResponse> => {
	const url = buildUrl("container_cleaning/list_pending_cleaning", urlParams);
	const response: AxiosResponse<PAContainerCleaningListResponse> = await axiosInstance.get(url, {
		headers: HEADERS,
	});
	return response.data;
};

const getContainerCleaningAgents = async (urlParams: any): Promise<CleaningAgentResponse> => {
	const url = buildUrl("container_cleaning/cleaning_agents", urlParams);
	const response: AxiosResponse<CleaningAgentResponse> = await axiosInstance.get(url, { headers: HEADERS });
	return response.data;
}

const postContainerCleaning = async (
	payload: ContainerCleaningPayload,
): Promise<any> => {
	const url = buildUrl("container_cleaning/save", {});
	const response: AxiosResponse<any> = await axiosInstance.post(url, payload, {
		headers: HEADERS,
	});
	return response.data;
};

const getContainersForInspection = async (urlParams: any): Promise<PhyInspContainerListResponse> => {
	const url = buildUrl("container_physical_inspection/list_containers", urlParams);
	const response: AxiosResponse<PhyInspContainerListResponse> = await axiosInstance.get(url, {
		headers: HEADERS,
	});
	return response.data;
};

const getContainerInspectionConditions = async (urlParams: any): Promise<PhyInspContainerConditionResponse> => {
	const url = buildUrl("container_physical_inspection/inspection_conditions", urlParams);
	const response: AxiosResponse<PhyInspContainerConditionResponse> = await axiosInstance.get(url, {
		headers: HEADERS,
	});
	return response.data;
};

const postContainerInspections = async (
	payload: ContInspectionPayload,
): Promise<any> => {
	const url = buildUrl("container_physical_inspection/save", {});
	const response: AxiosResponse<any> = await axiosInstance.post(url, payload, {
		headers: HEADERS,
	});
	return response.data;
};

const getAutoclaveEquipments = async (): Promise<AutoclaveEquipmentListResponse> => {
	const url = `${baseUrl}/equipments/list_all?sort=&order=desc&status=active&keyword=&process_id=10&id`;
	const response: AxiosResponse<AutoclaveEquipmentListResponse> = await axiosInstance.get(url, {
		headers: HEADERS,
	});
	return response.data;
};

const getContainersForSterilization = async (urlParams: any): Promise<ContainersSterilizationListResponse> => {
	const url = buildUrl("container_empty_sterilization/list_containers", urlParams);
	const response: AxiosResponse<ContainersSterilizationListResponse> = await axiosInstance.get(url, {
		headers: HEADERS,
	});
	return response.data;
};

const postContainerSterilization = async (payload: PostSterilizationPayload): Promise<any> => {
	const url = "container_empty_sterilization/save";
	const response: AxiosResponse<any> = await axiosInstance.post(url, payload, {
		headers: HEADERS,
	});
	return response.data;
};

//get all post autoclave batch list
const getPostAutoclaveBatchList = async (page: number, size: number, sort: string, order: string, mediaType: string, keyword: string) => {
	try {
		const response = await axiosInstance.get(`${baseUrl}/post_autoclave_batch/list_batches?page=${page}&size=${size}&sort=${sort}&order=${order}&media_type_id=${mediaType}&keyword=${keyword}`,
			{
				headers: {
					'x-auth-token': authToken,
					'x-userid': authUserID
				}
			});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return error.response?.data
		}
	}
};

//get post autoclave batch stat
const getPostAutoClaveBatchStat = async () => {
	try {
		const response = await axiosInstance.get(`${baseUrl}/post_autoclave_batch/summary_stats`,
			{
				headers: {
					'x-auth-token': authToken,
					'x-userid': authUserID
				}
			});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return error.response?.data
		}
	}
}


export const postAutoclaveServices = {
	getMediaBatches,
	getContainers,
	getMediaRemovalMethods,
	postMediaRemoval,

	getMediaCleaningContainerList,
	getContainerCleaningAgents,
	postContainerCleaning,

	getContainersForInspection,
	getContainerInspectionConditions,
	postContainerInspections,

	getAutoclaveEquipments,
	getContainersForSterilization,
	postContainerSterilization,

	getPostAutoclaveBatchList,
	getPostAutoClaveBatchStat
};
